import { useSelector } from 'react-redux';
import { animated } from 'react-spring';
import styled from 'styled-components';
import { endGame, isGameOver, setGameOver } from './ekrisReducer';
import PriceModule from 'ui/PriceModule/PriceModule';
import { PriceModuleLayoutTypeEnum } from '@inter-ikea-kompis/component-price-module';
import { getMarketProducts, getShoppingItemsInScene } from 'ducks/products';
import useFetchShoppingProducts from 'hooks/useFetchShoppingProducts';
import Button from 'ui/Button/Button';
import { useAppDispatch } from 'ducks/hooks';
import useRestartPlanner from 'hooks/useRestartPlanner';
import { usePlannerConfig } from 'contexts/plannerConfig';
import { EkrisFeature } from './EkrisFeature';
import ShapeFreeIcon from '@ingka/ssr-icon/paths/shape-free';
import StopIcon from '@ingka/ssr-icon/paths/stop';
import Title from 'ui/Typography/Title';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import Body from 'ui/Typography/Body';
import { ProductType } from '@inter-ikea-storageone/range';

const Container = styled(animated.div)`
  position: fixed;
  z-index: 20;
  bottom: 50%;
  left: calc(50% - (${props => props.theme.layout.sidebarWidth} / 2));
  transform: translate(-50%, 0%);
  margin: 0 auto 1.5rem;
  padding: 0 ${({ theme }) => theme.space.xs};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  outline: 2px solid #92462f;
  outline-offset: -10px;
`;

const ColoredContainer = styled(Container)`
  background: #fff;
  box-shadow: 0 2px 2px rgba(0, 0, 0, 0.1), 0 8px 8px rgba(0, 0, 0, 0.04);
  border-radius: ${({ theme }) => theme.space.xl};
  padding: 2rem;
  width: 40%;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 2rem;
`;

const EkrisGameOverModal = () => {
  const visible = useSelector(isGameOver);
  const productsInScene = useSelector(getShoppingItemsInScene);
  const { shoppingProducts } = useFetchShoppingProducts(productsInScene);
  const dispatch = useAppDispatch();
  const restartPlanner = useRestartPlanner();
  const marketProducts = useSelector(getMarketProducts);
  const {
    plannerConfig: { features }
  } = usePlannerConfig();
  const ekrisFeature = features.find(feature => feature.id === 'Ekris')?.feature;
  const eketFrames = marketProducts.filter(product => product.type === ProductType.EKET_FRAME);

  const handleRestart = () => {
    (ekrisFeature as EkrisFeature)?.getGame()?.restartGame(eketFrames, handleGameOver);
    dispatch(setGameOver(false));
    // Focus on canvas to enable keyboard controls after restarting the game
    const canvas: HTMLCanvasElement = (document.querySelector(
      '[data-testid="canvas"]'
    ) as unknown) as HTMLCanvasElement;

    if (canvas) {
      canvas.focus();
    }
  };

  const handleEndGame = () => {
    dispatch(endGame());
    dispatch(setGameOver(false));
    (ekrisFeature as EkrisFeature)?.getGame()?.stopGame();
    restartPlanner();
  };

  const handleGameOver = () => {
    dispatch(setGameOver(true));
  };

  return (
    visible && (
      <ColoredContainer data-testid="ekris-game-over">
        <Title mb="s" type={ThemeFontStyleTypeEnum.headingLarge}>
          <span style={{ color: '#92462F' }}>GAME OVER</span>
        </Title>
        <Body type={ThemeFontStyleTypeEnum.bodyMedium}>Your score:</Body>
        <PriceModule
          shoppingProducts={shoppingProducts}
          layout={PriceModuleLayoutTypeEnum.emphasized}
        />
        <ButtonWrapper>
          <Button
            type="primary"
            onClick={handleRestart}
            size={'small'}
            ssrIcon={ShapeFreeIcon}
            mr="s"
            buttonStyle={{ backgroundColor: '#4B4E40' }}
          >
            Start over
          </Button>
          <Button type="secondary" onClick={handleEndGame} size={'small'} ssrIcon={StopIcon}>
            Back to planner
          </Button>
        </ButtonWrapper>
      </ColoredContainer>
    )
  );
};
export default EkrisGameOverModal;
